import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getUserDiscovery, recordUserDiscoveryExposure } from '../../services/userDiscoveryApi';
import { resolveAvatar } from '../../utils/homeHelpers';

export function isUserDiscoveryUiEnabled(env = process.env) {
  return String(env.REACT_APP_USER_DISCOVERY_UI || '') === '1';
}

export function createExposureSessionId(cryptoApi = (typeof window !== 'undefined' ? window.crypto : null)) {
  return typeof cryptoApi?.randomUUID === 'function' ? cryptoApi.randomUUID().replace(/-/g, '') : '';
}

export function userFacingReason(reasonCodes = [], t) {
  if (reasonCodes.includes('zodiac_compatible')) return t('discovery.reasons.compatible');
  if (reasonCodes.includes('zodiac_same_sign')) return t('discovery.reasons.sameSign');
  if (reasonCodes.includes('zodiac_opposite_sign')) return t('discovery.reasons.opposite');
  return t('discovery.reasons.explore');
}

const panelStyle = {
  border: '1px solid rgba(218,165,32,.26)', background: 'rgba(6, 0, 18, .28)',
  boxShadow: '0 10px 24px rgba(0,0,0,.20)', borderRadius: 22, padding: '16px 14px', marginBottom: 12,
};
const defaultApi = { getUserDiscovery, recordUserDiscoveryExposure };

// An isolated home module: errors stay local and the parent dashboard remains usable.
export default function UserDiscoverySection({ onViewProfile, enabled = isUserDiscoveryUiEnabled(), api = defaultApi }) {
  const { t } = useTranslation('home');
  const [state, setState] = useState({ status: 'loading', data: null });
  const submitted = useRef(new Set());
  const exposureSessions = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    if (!enabled) return undefined;
    setState({ status: 'loading', data: null });
    api.getUserDiscovery()
      .then((data) => { if (!cancelled) setState({ status: 'success', data }); })
      .catch(() => { if (!cancelled) setState({ status: 'error', data: null }); });
    return () => { cancelled = true; };
  }, [api, enabled]);

  // This runs after React commits the successful card list.  A stable client
  // session ID and ref prevent duplicate exposure reports on re-render.
  useEffect(() => {
    if (!enabled || state.status !== 'success' || !state.data?.candidates?.length || !state.data?.recommendationSession) return;
    const setKey = `${state.data.recommendationSession}|${state.data.candidates.map((candidate) => candidate.user._id).join(',')}`;
    const exposureSessionId = exposureSessions.current.get(setKey) || createExposureSessionId();
    if (!exposureSessionId || submitted.current.has(exposureSessionId)) return;
    exposureSessions.current.set(setKey, exposureSessionId);
    submitted.current.add(exposureSessionId);
    void api.recordUserDiscoveryExposure({
      exposureSessionId,
      recommendationSession: state.data.recommendationSession,
      policyVersion: state.data.policyVersion,
      candidateIds: state.data.candidates.map((candidate) => candidate.user._id),
    }).catch(() => {});
  }, [api, enabled, state]);

  if (!enabled) return null;
  if (state.status === 'loading') return <section style={panelStyle} aria-label={t('discovery.title')}><span>{t('discovery.loading')}</span></section>;
  if (state.status === 'error') return <section style={panelStyle} aria-label={t('discovery.title')}><span>{t('discovery.error')}</span></section>;
  if (!state.data?.candidates?.length) return <section style={panelStyle} aria-label={t('discovery.title')}><strong>{t('discovery.title')}</strong><p>{t('discovery.empty')}</p></section>;

  return (
    <section style={panelStyle} aria-label={t('discovery.title')}>
      <div style={{ color: '#F3C96B', fontSize: '.72rem', fontWeight: 950, letterSpacing: '.20em', marginBottom: 6 }}>{t('discovery.kicker')}</div>
      <h2 style={{ fontSize: '1.1rem', margin: '0 0 6px' }}>{t('discovery.title')}</h2>
      <p style={{ margin: '0 0 13px', opacity: .82, fontSize: '.87rem' }}>{t('discovery.subtitle')}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {state.data.candidates.map((candidate) => (
          <article key={candidate.user._id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 10, borderRadius: 15, background: 'rgba(255,255,255,.055)' }}>
            <img src={resolveAvatar(candidate.user)} alt="" style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(218,165,32,.5)' }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{candidate.user.fullName || candidate.user.username}</strong>
              {candidate.user.username ? <span style={{ display: 'block', opacity: .75, fontSize: '.8rem' }}>@{candidate.user.username}</span> : null}
              <span style={{ display: 'block', color: '#F3C96B', fontSize: '.78rem', marginTop: 3 }}>{candidate.user.zodiacSign} · {userFacingReason(candidate.reasonCodes, t)}</span>
            </div>
            <button type="button" onClick={() => onViewProfile?.(candidate.user._id)} style={{ borderRadius: 999, border: '1px solid rgba(255,215,0,.55)', background: 'rgba(138,43,226,.45)', color: '#fff', padding: '8px 10px', cursor: 'pointer', fontWeight: 800, fontSize: '.75rem' }}>{t('discovery.viewProfile')}</button>
          </article>
        ))}
      </div>
    </section>
  );
}
